import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "calendar-content-text" }

import { PropType, computed, Ref, nextTick, watchEffect, watch, toRefs, onMounted } from 'vue';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { EventInput } from '@fullcalendar/vue3';
import FullCalendar, { CalendarOptions, DateSelectArg } from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
// import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { isNextMonth } from '@/utils/dateFormat';


export type CalendarEventInput = {
  title: '◯' | '×';
  selectable: boolean;
  start: string; // yyyy-MM-dd
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Calendar',
  props: {
  place: {
    type: String,
    default: '',
  },
  calendarEvents: {
    type: Array as PropType<CalendarEventInput[]>,
    default() {
      return [];
    },
  },
  selectedDate: {
    type: String, // yyyy-MM-dd
    default: '',
  },
  startDay: {
    type: String, // yyyy-MM-dd
    default: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  },
  periodMonth: {
    type: Number,
    default: 1,
  },
},
  emits: ["update:selectedDate", "update:selectedMonth"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:selectedDate', value: string): void;
  (e: 'update:selectedMonth', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props



const { selectedDate } = toRefs(props);



const handleDateSelect = (selectInfo: DateSelectArg) => {
  const selectDate = selectInfo.startStr;
  const selectable = props.calendarEvents.filter((i) => i.start === selectInfo.startStr)[0]?.selectable || false;

  if (selectable) {
    emit('update:selectedDate', selectDate);
  }
};

const calendarOptions: Ref<CalendarOptions> = computed(() => {
  return {
    plugins: [
      dayGridPlugin,
      interactionPlugin, // needed for dateClick
    ],
    locale: 'ja',
    headerToolbar: {
      left: 'prev',
      center: 'title',
      right: 'next',
    },
    dayCellContent: function (e) {
      e.dayNumberText = e.dayNumberText.replace('日', '');
    },
    initialView: 'dayGridMonth',
    events: props.calendarEvents as EventInput[], // alternatively, use the `events` setting to fetch from a feed
    contentHeight: 'auto',
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekends: true,
    select: handleDateSelect,
    selectLongPressDelay: 0,
    validRange: {
      start: props.startDay,
      end: format(endOfMonth(addMonths(new Date(props.startDay), props.periodMonth)), 'yyyy-MM-dd'),
    },
  };
});

nextTick(() => {
  const prevBtnEl = document.querySelector('.calendar .fc-prev-button');
  prevBtnEl?.addEventListener('click', () => {
    let selectedMonth = document.querySelector('.fc-toolbar-title')?.innerHTML.replace(/(年|月)/g, '');
    if (selectedMonth) {
      if (selectedMonth.length === 5) {
        selectedMonth = selectedMonth.replace(/^(\d{4})(\d{1})/, '$10$2');
      }
      emit('update:selectedMonth', selectedMonth);
    }
  });

  const nextBtnEl = document.querySelector('.calendar .fc-next-button');
  nextBtnEl?.addEventListener('click', () => {
    let selectedMonth = document.querySelector('.fc-toolbar-title')?.innerHTML.replace(/(年|月)/g, '');
    if (selectedMonth) {
      if (selectedMonth.length === 5) {
        selectedMonth = selectedMonth.replace(/^(\d{4})(\d{1})/, '$10$2');
      }
      emit('update:selectedMonth', selectedMonth);
    }
  });
});

const place = computed(() => props.place || '面談場所を選択してください');

watch(
  place,
  (place, oldPlace) => {
    nextTick(() => {
      // 場所を変更したら、選択された日をリセットする
      if (oldPlace !== undefined && oldPlace !== '面談場所を選択してください') {
        emit('update:selectedDate', '');
      }

      const titleParentEl = document.querySelectorAll('.calendar .fc-header-toolbar .fc-toolbar-chunk')[1];
      const titleEl = document.querySelector('.calendar .fc-toolbar-title');
      const placeEl = document.querySelector('.calendar .calendar-place');

      if (placeEl === null) {
        const newPlaceEl = document.createElement('div');
        newPlaceEl.className = 'calendar-place';
        newPlaceEl.innerHTML = place;
        if (titleEl !== null) {
          titleParentEl.insertBefore(newPlaceEl, titleEl.nextElementSibling);
        }
        return;
      }
      placeEl.innerHTML = place;
    });
  },
  { immediate: true }
);

watch(selectedDate, (newValue, oldValue) => {
  if (oldValue === '' && isNextMonth(newValue, props.startDay)) {
    const el = document.querySelector('.fc-next-button');
    if (el) {
      (el as HTMLButtonElement).click();
    }
  }
});

onMounted(() => {
  if (selectedDate.value !== '') {
    if (isNextMonth(selectedDate.value, props.startDay)) {
      const el = document.querySelector('.fc-next-button');
      if (el) {
        (el as HTMLButtonElement).click();
      }
    }
  }
});

watchEffect(() => {
  const selectedMonthDays = props.calendarEvents.map((i) => {
    const attribute = `[data-date="${i.start}"]`;
    return {
      ...i,
      attribute,
    };
  });
  selectedMonthDays.forEach((i) => {
    const el = document.querySelector(`${i.attribute}`);
    if (el === null) return;
    if (i.selectable) {
      el.classList.add('is-selectable');
    } else {
      el.classList.remove('is-selectable');
    }
  });

  const nonSelectedEls = document.querySelectorAll('.fc-daygrid-day-frame');
  nonSelectedEls.forEach((el) => {
    el.classList.remove('is-selected');
  });

  const selectedEl = document.querySelector(`[data-date="${selectedDate.value}"] .fc-daygrid-day-frame`);
  if (selectedEl === null) return;
  selectedEl.classList.add('is-selected');
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(FullCalendar), {
    class: "calendar",
    options: _unref(calendarOptions)
  }, {
    eventContent: _withCtx((arg) => [
      _createElementVNode("i", _hoisted_1, _toDisplayString(arg.event.title), 1)
    ]),
    _: 1
  }, 8, ["options"]))
}
}

})